import * as THREE from 'three';
import { CONFIG } from './config.js';
import { clamp, easeInOutQuad, lerp } from '../utils/math.js';

export class Player {
  constructor(scene, materials) {
    this.lane = 1; // mid
    this.targetLane = 1;
    this.laneT = 1;
    this.laneFromX = 0;
    this.x = 0;
    this.y = 0;
    this.z = 0;
    this.forwardSpeed = CONFIG.initialSpeed;
    this.distance = 0;

    this.jumping = false;
    this.jumpT = 0;
    this.rolling = false;
    this.rollT = 0;
    this.onTrainTop = false;
    this.alive = true;
    this.invuln = 0;

    this.group = new THREE.Group();
    this.body = new THREE.Mesh(
      new THREE.CapsuleGeometry(CONFIG.playerCapsuleRadius, CONFIG.playerCapsuleHeight - CONFIG.playerCapsuleRadius * 2, 6, 12),
      materials.player
    );
    this.body.castShadow = true;
    this.body.position.y = CONFIG.playerCapsuleHeight / 2;
    this.group.add(this.body);

    this.boardMesh = new THREE.Mesh(
      new THREE.BoxGeometry(0.7, 0.12, 1.2),
      materials.board
    );
    this.boardMesh.position.y = 0.08;
    this.boardMesh.visible = false;
    this.group.add(this.boardMesh);

    scene.add(this.group);
    this.materials = materials;
    this._baseScaleY = 1;
  }

  reset() {
    this.lane = 1;
    this.targetLane = 1;
    this.laneT = 1;
    this.x = 0;
    this.y = 0;
    this.z = 0;
    this.forwardSpeed = CONFIG.initialSpeed;
    this.distance = 0;
    this.jumping = false;
    this.jumpT = 0;
    this.rolling = false;
    this.rollT = 0;
    this.onTrainTop = false;
    this.alive = true;
    this.invuln = 0;
    this.boardMesh.visible = false;
    this.body.scale.set(1, 1, 1);
    this.body.material = this.materials.player;
    this.syncMesh();
  }

  requestLane(dir) {
    if (!this.alive) return false;
    const next = clamp(this.targetLane + dir, 0, CONFIG.lanes - 1);
    if (next === this.targetLane) return false;
    this.laneFromX = this.x;
    this.lane = this.targetLane;
    this.targetLane = next;
    this.laneT = 0;
    return true;
  }

  requestJump() {
    if (!this.alive || this.jumping || this.rolling) return false;
    this.jumping = true;
    this.jumpT = 0;
    this.onTrainTop = false;
    return true;
  }

  requestRoll() {
    if (!this.alive || this.rolling) return false;
    if (this.jumping) {
      // cancel jump into roll (land fast)
      this.jumping = false;
      this.jumpT = 0;
      this.y = 0;
    }
    this.rolling = true;
    this.rollT = 0;
    this.body.material = this.materials.playerRoll;
    return true;
  }

  setHoverboard(on) {
    this.boardMesh.visible = on;
  }

  update(dt, world) {
    if (!this.alive) return;

    // Speed ramp toward target with distance
    const t = clamp(this.distance / CONFIG.speedRampDistance, 0, 1);
    this.forwardSpeed = lerp(CONFIG.initialSpeed, CONFIG.targetSpeed, t);
    this.z += this.forwardSpeed * dt;
    this.distance = this.z;

    // Lane change
    if (this.laneT < 1) {
      this.laneT = clamp(this.laneT + dt / CONFIG.laneChangeDuration, 0, 1);
      const e = easeInOutQuad(this.laneT);
      const toX = CONFIG.laneXs[this.targetLane];
      this.x = lerp(this.laneFromX, toX, e);
      if (this.laneT >= 1) this.lane = this.targetLane;
    } else {
      this.x = CONFIG.laneXs[this.targetLane];
    }

    // Jump parabola
    if (this.jumping) {
      this.jumpT += dt / CONFIG.jumpDuration;
      const jt = clamp(this.jumpT, 0, 1);
      this.y = Math.sin(jt * Math.PI) * CONFIG.jumpHeight;
      if (this.jumpT >= 1) {
        this.jumping = false;
        this.jumpT = 0;
        // optional train-top land
        if (world?.isTrainTopAt(this.x, this.z)) {
          this.onTrainTop = true;
          this.y = 2.2;
        } else {
          this.y = 0;
          this.onTrainTop = false;
        }
      }
    } else if (this.onTrainTop) {
      if (world?.isTrainTopAt(this.x, this.z)) {
        this.y = 2.2;
      } else {
        this.onTrainTop = false;
        this.y = 0;
      }
    } else {
      this.y = 0;
    }

    // Roll
    if (this.rolling) {
      this.rollT += dt / CONFIG.rollDuration;
      const s = CONFIG.rollHeightScale;
      this.body.scale.set(1.15, s, 1.1);
      this.body.position.y = (CONFIG.playerCapsuleHeight * s) / 2;
      if (this.rollT >= 1) {
        this.rolling = false;
        this.rollT = 0;
        this.body.scale.set(1, 1, 1);
        this.body.position.y = CONFIG.playerCapsuleHeight / 2;
        this.body.material = this.materials.player;
      }
    }

    if (this.invuln > 0) this.invuln = Math.max(0, this.invuln - dt);

    this.syncMesh();
  }

  syncMesh() {
    this.group.position.set(this.x, this.y, this.z);
    const lean = (this.targetLane - this.lane) * (1 - this.laneT) * 0.25;
    this.group.rotation.z = -lean;
  }

  /** Hit capsule approx in world space */
  getBounds() {
    const h = this.rolling
      ? CONFIG.playerCapsuleHeight * CONFIG.rollHeightScale
      : CONFIG.playerCapsuleHeight;
    const r = CONFIG.playerCapsuleRadius;
    return {
      x: this.x,
      y: this.y,
      z: this.z,
      halfW: r + 0.1,
      height: h,
      rolling: this.rolling,
      jumping: this.jumping || this.y > 0.4,
      invuln: this.invuln > 0,
    };
  }
}
